/**
 * Onboarding Funnels Page
 *
 * Shows user acquisition funnel and org setup funnel side by side.
 * Uses FunnelChart for visualization with clickable drill-down.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { founderDashboardAPI } from '../../../services/api';
import { useEnvironment } from '../../../context/EnvironmentContext';
import FunnelChart from '../../../components/founder-dashboard/FunnelChart';
import RefreshIndicator from '../../../components/founder-dashboard/RefreshIndicator';

// User funnel stages with descriptions.
// Key order drives the funnel chart column order via Object.keys().
// NOTE: notify_me will show 0 until the user_funnel snapshot aggregator is
// updated to derive it — see onboardingFunnelSnapshotJob.js computeUserFunnel.
const USER_STAGES = {
  signed_up: { label: 'Signed Up', tip: 'Created an account (Google, Apple, or email). Still pending_org — no church yet.' },
  viewed_options: { label: 'Opened Wizard', tip: 'Opened the Getting Started wizard to explore options.' },
  onboarding_skipped: { label: 'Closed Wizard', tip: 'Closed the Getting Started wizard without taking an action.' },
  searched_church: { label: 'Searched Church', tip: 'Used the church search to look for their church by name.' },
  notify_me: { label: 'Notify Me', tip: 'One-tap "notify me when my church joins" — email only, no city/state/description.' },
  lead_submitted: { label: 'Submitted Lead', tip: 'Church not on Flock — submitted interest form with city, state, and/or a message.' },
  join_code_attempted: { label: 'Tried Join Code', tip: 'Entered a group join code to try joining directly.' },
  join_request_sent: { label: 'Sent Join Request', tip: 'Found their church and submitted a request to join.' },
  awaiting_approval: { label: 'Awaiting Approval', tip: 'Join request pending — waiting for a church admin to approve.' },
  invited: { label: 'Invited', tip: 'Admin approved their join request and sent an invitation.' },
  personal_group_started: { label: 'Personal Group', tip: 'Started the personal group creation wizard.' },
  org_creation_started: { label: 'Started Org Creation', tip: 'Began creating a new organization (church leader path).' },
  activated: { label: 'Activated', tip: 'Joined an organization — fully onboarded and active.' },
};

// Org setup steps with descriptions
const ORG_STEPS = {
  org_created: { label: 'Organization Created', tip: 'Church organization record exists in the system.' },
  email_verified: { label: 'Email Verified', tip: 'Admin verified their email address.' },
  first_login: { label: 'First Login', tip: 'An admin logged into the app for the first time.' },
  campus_added: { label: 'Campus Added', tip: 'At least one campus/location has been created.' },
  first_group: { label: 'First Group Created', tip: 'At least one community group exists (e.g., small group, Bible study).' },
  second_admin: { label: 'Second Admin Invited', tip: 'A second leader has been given a staff role (pastor, director, etc.).' },
  members_invited: { label: 'Members Invited', tip: 'More than one member has joined the organization.' },
  join_codes: { label: 'Join Codes Active', tip: 'Groups have join codes enabled for scalable member acquisition.' },
  first_prayer: { label: 'First Prayer', tip: 'At least one prayer request has been submitted in the org.' },
  first_chat: { label: 'First Chat Message', tip: 'At least one chat message has been sent in a group.' },
  first_event: { label: 'First Event', tip: 'At least one event has been created.' },
  first_announcement: { label: 'First Announcement', tip: 'At least one announcement has been posted.' },
};

const PERIOD_OPTIONS = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
];

export default function OnboardingFunnelsPage() {
  const { currentEnv } = useEnvironment();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [period, setPeriod] = useState('all');

  const fetchData = async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const response = await founderDashboardAPI.getOnboardingFunnels({
        refresh,
        period: period === 'all' ? null : period,
      });
      setData(response.data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('[OnboardingFunnels] Error fetching data:', err);
      setError(
        err.response?.data?.error || err.message || 'Failed to load data'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setData(null);
    setError(null);
    fetchData();
    const interval = setInterval(() => fetchData(), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentEnv, period]);

  // Transform user funnel API data into stages array for FunnelChart
  const buildUserStages = () => {
    const stages = data?.userFunnel?.stages || {};
    const total = data?.userFunnel?.total || 0;
    const stageOrder = Object.keys(USER_STAGES);
    const maxCount = Math.max(total, 1);

    return stageOrder.map((key) => ({
      name: key,
      label: USER_STAGES[key].label,
      tooltip: USER_STAGES[key].tip,
      count: stages[key] || 0,
      percentage: Math.round(((stages[key] || 0) / maxCount) * 100),
    }));
  };

  // Transform org funnel aggregate data into stages array
  const buildOrgStages = () => {
    const aggregate = data?.orgFunnel?.aggregate || {};
    const stepOrder = Object.keys(ORG_STEPS);
    const maxCount = Math.max(aggregate[stepOrder[0]] || 0, 1);

    return stepOrder.map((key) => ({
      name: key,
      label: ORG_STEPS[key].label,
      tooltip: ORG_STEPS[key].tip,
      count: aggregate[key] || 0,
      percentage: Math.round(((aggregate[key] || 0) / maxCount) * 100),
    }));
  };

  const handleUserStageClick = (stage) => {
    navigate(`/onboarding/funnels/users/${stage}`);
  };

  const handleOrgStepClick = (step) => {
    navigate(`/onboarding/funnels/orgs/${step}`);
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-800 font-medium">
          Couldn't load onboarding funnels
        </p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={() => fetchData()}
          className="mt-4 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors min-h-[44px]"
        >
          Try Again
        </button>
      </div>
    );
  }

  const userStages = buildUserStages();
  const orgStages = buildOrgStages();

  return (
    <div className="space-y-6">
      {/* Period Filter + Refresh */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200 p-1">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors min-h-[36px] ${
                period === opt.value
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <RefreshIndicator
          lastUpdated={lastUpdated}
          onRefresh={() => fetchData(true)}
          loading={loading}
        />
      </div>

      {/* User Acquisition Funnel */}
      <FunnelChart
        title={`User Acquisition Funnel${period !== 'all' ? ` (${PERIOD_OPTIONS.find(o => o.value === period)?.label})` : ''}`}
        stages={userStages}
        onStageClick={handleUserStageClick}
        loading={loading}
        colorScheme="blue"
      />

      {/* Organization Setup Funnel */}
      <FunnelChart
        title="Organization Setup Funnel"
        stages={orgStages}
        onStageClick={handleOrgStepClick}
        loading={loading}
        colorScheme="emerald"
      />
    </div>
  );
}
